// components/footfall/pulseCommands.ts
import type { RefObject } from 'react';
import { PLAN_EVIDENCE_COLLECTIONS } from '#lib/footfall/assetPlanEvidenceData';
import { moveError, potentialRent, referenceRentError, yieldError } from '#lib/footfall/scenario';
import type { KernelHandle, PulseLayer, PulseMove, UiCommandDefinition } from '#types/types';
import { requestListPermission } from '#lib/list/listPermission';
import { euro } from '#lib/footfall/pulse';
import { childrenOf, layoutError } from '#lib/footfall/layout';
import { derivePulseFromState, scenarioLabel } from './pulseDerive';
import { addLayoutChange, removeLayoutChange, createScenario, editDraft, markAgentEdit, newMove, patchPulse, selectShop, setMove, setScenarioThesis, switchScenario } from './pulseState';

const LAYERS: PulseLayer[] = ['mix', 'occupancy', 'traffic', 'reviews'];
const MOVE_TYPES: PulseMove['type'][] = ['relet', 'vacate'];

/**
 * The open plan, refused when it is not the plan this conversation belongs to: a turn started on one plan must never
 * write into another the user has since opened.
 */
function openPlan(action: string, kernel: RefObject<KernelHandle | null>, sessionId: string) {
  const k = kernel.current;
  if (!k) throw new Error(`${action}: the app is not ready.`);
  const state = k.getState();
  const session = state.inventory.ChatSession.registry[sessionId];
  if (!state.pulse.planId || !state.pulse.draft) throw new Error(`${action}: no plan is open. Ask the user to open the plan from the Plan dashboard.`);
  if (session?.creationModule === 'pulse' && session.creationEntityId !== state.pulse.planId) throw new Error(`${action}: the plan on screen is not the one this conversation belongs to. Ask the user to reopen "${session.name}".`);
  return { k, w: derivePulseFromState(state) };
}

function shopOf(action: string, w: ReturnType<typeof derivePulseFromState>, shopId: unknown, places = w.places) {
  const id = typeof shopId === 'string' ? shopId.trim() : '';
  // The landlord's code (B97) is what the user says; it resolves only when it names one unit.
  const byCode = places.filter(p => p.feature.code?.toLowerCase() === id.toLowerCase());
  if (byCode.length > 1) throw new Error(`${action}: "${id}" is the landlord code of several units (${byCode.map(p => p.feature.id).join(', ')}); use the shop id.`);
  const place = places.find(p => p.feature.id === id) ?? byCode[0];
  if (!place) throw new Error(`${action}: no shop "${id}" on this centre. Use a shop id from the SHOPS block (for example ${w.places[0]?.feature.id ?? 'E0-001'}).`);
  return place;
}

const optionalNumber = (action: string, name: string, value: unknown): number | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) throw new Error(`${action}: "${name}" must be a number.`);
  return n;
};

export const PULSE_COMMAND_DEFINITIONS: Record<string, UiCommandDefinition> = {
  PULSE_READ_TRAFFIC_EVIDENCE: {
    action_type: 'PULSE_READ_TRAFFIC_EVIDENCE',
    traceLabels: { active: 'reading', done: 'read', unit: 'evidence' },
    targetEntityType: 'ABSOLUTE',
    description: 'Reads immutable evidence for the open Plan asset. Returns coverage, limitations and a bounded page of native records. Indoor is source-declared mapping; directional is employed residence/work relationships; attendance is historical claims, never measured visitor calibration. Follow nextOffset to read further records.',
    parameters: [
      { name: 'dataset', type: 'string', description: 'indoor, directional or attendance.', required: true, options: ['indoor', 'directional', 'attendance'] },
      { name: 'collection', type: 'string', description: 'Optional indoor collection: effectiveUnits (default), originalUnits, places, profiles, declarations, reclassifications or nameActivityCandidates. Directional: observations (default), sourceExceptions or endpoints. Attendance: references (default) or claimGroups.', required: false },
      { name: 'offset', type: 'number', description: 'Zero-based record offset, default 0.', required: false },
      { name: 'limit', type: 'number', description: 'Record limit, default 25, maximum 50; byte limit may shorten a page explicitly.', required: false },
    ],
    availableInModes: ['pulse'], headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_READ_TRAFFIC_EVIDENCE';
      const { k, w } = openPlan(action, kernel, options.sessionId);
      const before = k.getState();
      const activeSession = k.getActiveChatSessionId();
      const session = before.inventory.ChatSession.registry[options.sessionId];
      if (!session || session.creationModule !== 'pulse' || session.creationEntityId !== before.pulse.planId || activeSession !== options.sessionId) throw new Error(`${action}: only the active conversation for the open plan can read its evidence.`);
      const descriptor = w.property?.trafficEvidenceData;
      const propertyId = before.pulse.propertyId;
      const planId = before.pulse.planId, ownerKey = before.pulse.ownerKey;
      const sessionEntityId = session?.creationEntityId, sessionModule = session?.creationModule;
      const auditId = descriptor?.auditId, revision = descriptor?.revision;
      if (!propertyId || !descriptor || !auditId || !revision) throw new Error(`${action}: no verified evidence bundle is loaded for this asset.`);
      const dataset: unknown = params.dataset;
      if (dataset !== 'indoor' && dataset !== 'directional' && dataset !== 'attendance') throw new Error(`${action}: invalid dataset.`);
      const offset = params.offset ?? 0, limit = params.limit ?? 25;
      if (typeof offset !== 'number' || !Number.isSafeInteger(offset) || offset < 0 || typeof limit !== 'number' || !Number.isSafeInteger(limit) || limit < 1 || limit > 50) throw new Error(`${action}: offset and limit must be whole numbers; limit is 1–50.`);
      const collections: readonly string[] = PLAN_EVIDENCE_COLLECTIONS[dataset];
      const collection = params.collection ?? collections[0]!;
      if (typeof collection !== 'string' || !collections.includes(collection)) throw new Error(`${action}: invalid collection.`);
      const response = await k.readPropertyTrafficEvidence(propertyId, auditId, dataset, { collection, offset, limit });
      const after = openPlan(action, kernel, options.sessionId);
      const current = after.k.getState();
      const currentSession = current.inventory.ChatSession.registry[options.sessionId];
      if (!currentSession || currentSession.creationModule !== 'pulse' || currentSession.creationEntityId !== planId || after.k.getActiveChatSessionId() !== options.sessionId) throw new Error(`${action}: the active plan conversation changed while reading.`);
      if (after.k !== k || current.pulse.planId !== planId || current.pulse.propertyId !== propertyId || current.pulse.ownerKey !== ownerKey || after.k.getActiveChatSessionId() !== activeSession || current.inventory.ChatSession.registry[options.sessionId]?.creationEntityId !== sessionEntityId || current.inventory.ChatSession.registry[options.sessionId]?.creationModule !== sessionModule || after.w.property?.trafficEvidenceData?.auditId !== auditId || after.w.property?.trafficEvidenceData?.revision !== revision || response.revision !== revision) throw new Error(`${action}: the open plan or evidence changed while reading; retry on the current plan.`);
      if (response.schema !== 'fater-plan-traffic-evidence-page-v1') throw new Error(`${action}: evidence page unavailable.`);
      params.result = response;
    },
  },

  PULSE_SELECT_SHOP: {
    action_type: 'PULSE_SELECT_SHOP',
    traceLabels: { active: 'showing', done: 'showed', unit: 'shop' },
    targetEntityType: 'ABSOLUTE',
    description: 'Selects a shop of the open plan: highlights it on the map (switching level if needed) and opens it in the right panel. Use it to show the user the shop you are talking about.',
    parameters: [{ name: 'shopId', type: 'string', description: 'Shop id from the SHOPS block, e.g. "E0-041".', required: true }],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const { k, w } = openPlan('PULSE_SELECT_SHOP', kernel, options.sessionId);
      const place = shopOf('PULSE_SELECT_SHOP', w, params.shopId);
      selectShop(k, place.feature.id);
      markAgentEdit(k, place.feature.id);
    },
  },

  PULSE_SET_MOVE: {
    action_type: 'PULSE_SET_MOVE',
    traceLabels: { active: 'planning', done: 'planned', unit: 'move' },
    targetEntityType: 'ABSOLUTE',
    description: 'Creates or replaces the move on one shop of the open plan: re-let it to a shortlisted brand, or vacate it. Omitted fields keep the shop\'s existing move values; a new re-let without a target rent gets the shop\'s potential rent. One move per shop.',
    parameters: [
      { name: 'shopId', type: 'string', description: 'Shop id from the SHOPS block.', required: true },
      { name: 'type', type: 'string', description: '"relet" or "vacate".', required: true, options: MOVE_TYPES },
      { name: 'brandId', type: 'string', description: 'For a re-let: the brand id from the SHORTLIST block. Omit to leave the tenant to choose.', required: false },
      { name: 'rentPerSqm', type: 'number', description: 'For a re-let: target rent in €/m²/year.', required: false },
      { name: 'start', type: 'string', description: 'Start date YYYY-MM-DD, not in the past. Default: the first of the month six months from now.', required: false },
      { name: 'rentFreeMonths', type: 'number', description: 'Whole months of rent-free, 0 to 36.', required: false },
      { name: 'works', type: 'number', description: 'Landlord works in €, 0 or more.', required: false },
    ],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_SET_MOVE';
      const { k, w } = openPlan(action, kernel, options.sessionId);
      const place = shopOf(action, w, params.shopId);
      const type = params.type as PulseMove['type'];
      if (!MOVE_TYPES.includes(type)) throw new Error(`${action}: "type" must be "relet" or "vacate".`);
      if (type === 'vacate' && place.category === 'VACANT') throw new Error(`${action}: ${place.feature.id} is already vacant; re-let it instead.`);
      const existing = w.scenario.moves.find(m => m.shopId === place.feature.id);
      let brand = existing?.type === 'relet' ? { brandId: existing.brandId, brandName: existing.brandName, brandCategory: existing.brandCategory } : { brandId: null, brandName: null, brandCategory: null };
      if (type === 'relet' && params.brandId !== undefined && params.brandId !== null && params.brandId !== '') {
        if (w.shortlistStatus !== 'ready') throw new Error(`${action}: the shortlist is still loading; retry in a moment.`);
        const hit = w.shortlist.find(b => b.id === params.brandId) ?? w.shortlist.find(b => b.name.toLowerCase() === String(params.brandId).toLowerCase());
        if (!hit) throw new Error(`${action}: "${params.brandId}" is not on this asset's Source shortlist. Use a brand id from the SHORTLIST block; a brand that is not shortlisted needs a sourcing in Source first.`);
        brand = { brandId: hit.id, brandName: hit.name, brandCategory: hit.category };
      }
      const percentile = place.feature.exposure?.percentile;
      const potential = w.scenario.referenceRent && percentile !== undefined ? potentialRent(w.scenario.referenceRent, percentile) : null;
      const rent = optionalNumber(action, 'rentPerSqm', params.rentPerSqm);
      const months = optionalNumber(action, 'rentFreeMonths', params.rentFreeMonths);
      const works = optionalNumber(action, 'works', params.works);
      const move: PulseMove = type === 'vacate'
        ? newMove(place.feature.id, { type, start: typeof params.start === 'string' ? params.start : existing?.start })
        : newMove(place.feature.id, {
          ...existing, ...brand, type,
          rentPerSqm: rent !== undefined ? rent : existing?.rentPerSqm ?? potential,
          ...(typeof params.start === 'string' ? { start: params.start } : {}),
          ...(months !== undefined ? { rentFreeMonths: months ?? 0 } : {}),
          ...(works !== undefined ? { works: works ?? 0 } : {}),
        });
      const refusal = moveError(move, undefined, existing?.start);
      if (refusal) throw new Error(`${action}: ${refusal}`);
      setMove(k, move);
      selectShop(k, place.feature.id);
      markAgentEdit(k, place.feature.id);
    },
  },

  PULSE_REMOVE_MOVE: {
    action_type: 'PULSE_REMOVE_MOVE',
    traceLabels: { active: 'removing', done: 'removed', unit: 'move' },
    targetEntityType: 'ABSOLUTE',
    description: 'Removes the move on one shop of the open plan; the shop goes back to how it is today.',
    parameters: [{ name: 'shopId', type: 'string', description: 'Shop id carrying a move.', required: true }],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const { k, w } = openPlan('PULSE_REMOVE_MOVE', kernel, options.sessionId);
      const place = shopOf('PULSE_REMOVE_MOVE', w, params.shopId);
      if (!w.scenario.moves.some(m => m.shopId === place.feature.id)) throw new Error(`PULSE_REMOVE_MOVE: ${place.feature.id} has no move in this plan.`);
      editDraft(k, d => ({ ...d, moves: d.moves.filter(m => m.shopId !== place.feature.id) }));
      markAgentEdit(k, place.feature.id);
    },
  },

  PULSE_SET_ASSUMPTIONS: {
    action_type: 'PULSE_SET_ASSUMPTIONS',
    traceLabels: { active: 'setting', done: 'set', unit: 'assumption' },
    targetEntityType: 'ABSOLUTE',
    description: 'Proposes the plan\'s pricing assumptions: the reference rent (€/m²/year, the centre\'s typical prime rent, 1 to 5,000) and the yield (%, 2 to 15). The user approves or declines them on a card that replaces the composer, and the command returns their answer: this IS the question, so never ask for these values in prose first. A decline throws; ask what values they want.',
    parameters: [
      { name: 'referenceRent', type: 'number', description: 'Reference rent in €/m²/year.', required: false },
      { name: 'yieldPct', type: 'number', description: 'Yield in percent, e.g. 5.5.', required: false },
      { name: 'basis', type: 'string', description: 'Where the values come from, in one short line the user reads on the card, e.g. "Knight Frank end-2025 prime yields; court-reviewed rents at the centre". Say plainly when a value is an estimate.', required: true },
    ],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_SET_ASSUMPTIONS';
      const { k } = openPlan(action, kernel, options.sessionId);
      const ref = optionalNumber(action, 'referenceRent', params.referenceRent);
      const yld = optionalNumber(action, 'yieldPct', params.yieldPct);
      if (ref === undefined && yld === undefined) throw new Error(`${action}: give referenceRent, yieldPct, or both.`);
      const refusal = (ref !== undefined && referenceRentError(ref)) || (yld !== undefined && yieldError(yld));
      if (refusal) throw new Error(`${action}: ${refusal}`);
      const basis = typeof params.basis === 'string' ? params.basis.trim().slice(0, 300) : '';
      if (!basis) throw new Error(`${action}: give the "basis" of these values.`);
      const fr = k.getState().language === 'fr';
      const allowed = await requestListPermission(k, options.sessionId, {
        action,
        title: fr ? 'Utiliser ces hypothèses de prix ?' : 'Use these pricing assumptions?',
        label: fr ? 'Hypothèses' : 'Assumptions',
        items: [
          ...(ref !== undefined ? [{ id: 'rent', name: fr ? `Loyer de référence ${euro(ref, fr)}/m²/an` : `Reference rent ${euro(ref, fr)}/m²/year` }] : []),
          ...(yld !== undefined ? [{ id: 'yield', name: fr ? `Taux de rendement ${yld} %` : `Yield ${yld}%` }] : []),
          { id: 'basis', name: basis },
        ],
      }, options.abortSignal);
      if (!allowed) throw new Error(`${action}: the user declined these assumptions. Nothing was set; ask which values they want.`);
      editDraft(k, d => ({ ...d, ...(ref !== undefined ? { referenceRent: ref } : {}), ...(yld !== undefined ? { yieldPct: yld } : {}) }));
    },
  },

  PULSE_ZOOM: {
    action_type: 'PULSE_ZOOM',
    traceLabels: { active: 'zooming', done: 'zoomed', unit: 'view' },
    targetEntityType: 'ABSOLUTE',
    description: 'Moves the map\'s camera to frame the shops you are showing, on their level, so the user can see them; with no shopIds it frames the whole level again. Use it whenever you point at specific shops: the whole centre on screen makes a single unit unreadable.',
    parameters: [{ name: 'shopIds', type: 'array', description: 'Shop ids to frame together, all on one level (at most 20). Omit to show the whole level.', required: false }],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_ZOOM';
      const { k, w } = openPlan(action, kernel, options.sessionId);
      const raw = params.shopIds === undefined ? [] : params.shopIds;
      if (!Array.isArray(raw) || raw.length > 20) throw new Error(`${action}: "shopIds" must be a list of at most 20 shop ids.`);
      const places = raw.map(id => shopOf(action, w, id));
      const levels = [...new Set(places.map(p => p.feature.level))];
      if (levels.length > 1) throw new Error(`${action}: these shops are on different levels (${levels.join(', ')}); zoom on one level at a time.`);
      const levelId = levels[0];
      if (levelId && levelId !== w.level?.id) editDraft(k, d => ({ ...d, levelId }));
      patchPulse(k, { zoom: { shopIds: places.map(p => p.feature.id), at: Date.now() } });
    },
  },

  PULSE_SET_VIEW: {
    action_type: 'PULSE_SET_VIEW',
    traceLabels: { active: 'switching', done: 'switched', unit: 'view' },
    targetEntityType: 'ABSOLUTE',
    description: 'Changes what the map shows: the level, and the colouring layer (mix = trade, occupancy, traffic = passing-traffic percentile, reviews = Google reviews).',
    parameters: [
      { name: 'layer', type: 'string', description: 'Colouring layer.', required: false, options: LAYERS },
      { name: 'levelId', type: 'string', description: 'Level id from the LEVELS line.', required: false },
    ],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_SET_VIEW';
      const { k, w } = openPlan(action, kernel, options.sessionId);
      if (params.layer !== undefined && !LAYERS.includes(params.layer)) throw new Error(`${action}: "layer" must be one of ${LAYERS.join(', ')}.`);
      if (params.levelId !== undefined && !w.levels.some(l => l.id === params.levelId)) throw new Error(`${action}: no level "${params.levelId}". Levels: ${w.levels.map(l => l.id).join(', ')}.`);
      editDraft(k, d => ({ ...d, ...(params.layer ? { layer: params.layer as PulseLayer } : {}), ...(params.levelId ? { levelId: params.levelId as string } : {}) }));
    },
  },

  PULSE_SPLIT_UNIT: {
    action_type: 'PULSE_SPLIT_UNIT',
    traceLabels: { active: 'splitting', done: 'split', unit: 'unit' },
    targetEntityType: 'ABSOLUTE',
    description: 'Cuts one unit of the centre into 2 to 4 smaller units in the scenario on the map, across its length, and returns the new units\' ids and areas. The unit\'s current tenant and rent go (the scenario gives that rent up); re-let each new unit with PULSE_SET_MOVE. Use it when a big box earns less per m² than smaller shops would on the same footfall, or when shortlisted brands want smaller units than the centre has.',
    parameters: [
      { name: 'shopId', type: 'string', description: 'The unit to split: shop id or landlord code, as it stands today (not a unit a scenario already created).', required: true },
      { name: 'shares', type: 'array', description: 'Each new unit\'s share of the area, in order along the unit\'s length, adding up to 1 (e.g. [0.5, 0.25, 0.25]); each at least 0.1. Or give "parts" for equal shares.', required: false },
      { name: 'parts', type: 'number', description: 'Number of equal new units, 2 to 4, when no shares are given.', required: false },
      { name: 'works', type: 'number', description: 'Estimated works in € for the cut: partitions, new shopfronts, separate services. Required: state the basis in the reasoning.', required: true },
    ],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_SPLIT_UNIT';
      const { k, w } = openPlan(action, kernel, options.sessionId);
      const parent = shopOf(action, w, params.shopId, w.todayPlaces);
      const parts = Number(params.parts);
      const shares = Array.isArray(params.shares) && params.shares.length ? params.shares.map(Number) : Number.isInteger(parts) ? Array.from({ length: parts }, () => 1 / parts) : [];
      const works = Number(params.works);
      const change = { kind: 'split' as const, shopId: parent.feature.id, shares, works };
      const refusal = w.centre ? layoutError(w.centre, change, k.getState().pulse.draft?.layout ?? []) : 'The centre has no plan.';
      if (refusal) throw new Error(`${action}: ${refusal}`);
      addLayoutChange(k, change);
      markAgentEdit(k, childrenOf(change)[0]!);
      const after = derivePulseFromState(k.getState()).places;
      params.result = { units: childrenOf(change).map(id => { const f = after.find(p => p.feature.id === id)?.feature; return { id, code: f?.code ?? null, areaSqm: f?.areaSqm ?? null }; }) };
    },
  },

  PULSE_MERGE_UNITS: {
    action_type: 'PULSE_MERGE_UNITS',
    traceLabels: { active: 'merging', done: 'merged', unit: 'unit' },
    targetEntityType: 'ABSOLUTE',
    description: 'Joins 2 to 4 neighbouring units on one level into one in the scenario on the map, and returns the new unit\'s id and area. Their tenants and rents go; re-let the new unit with PULSE_SET_MOVE. Use it when a shortlisted brand needs more space than any one unit offers, or when small weak units would earn more as one.',
    parameters: [
      { name: 'shopIds', type: 'array', description: 'The units to join (ids or landlord codes, as they stand today); they must share walls.', required: true },
      { name: 'works', type: 'number', description: 'Estimated works in € to join them. Required: state the basis in the reasoning.', required: true },
    ],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_MERGE_UNITS';
      const { k, w } = openPlan(action, kernel, options.sessionId);
      if (!Array.isArray(params.shopIds)) throw new Error(`${action}: "shopIds" must be a list of 2 to 4 units.`);
      const change = { kind: 'merge' as const, shopIds: params.shopIds.map(id => shopOf(action, w, id, w.todayPlaces).feature.id), works: Number(params.works) };
      const refusal = w.centre ? layoutError(w.centre, change, k.getState().pulse.draft?.layout ?? []) : 'The centre has no plan.';
      if (refusal) throw new Error(`${action}: ${refusal}`);
      addLayoutChange(k, change);
      const [id] = childrenOf(change);
      markAgentEdit(k, id!);
      const unit = derivePulseFromState(k.getState()).places.find(p => p.feature.id === id)?.feature;
      params.result = { unit: { id, code: unit?.code ?? null, areaSqm: unit?.areaSqm ?? null } };
    },
  },

  PULSE_RESTORE_UNIT: {
    action_type: 'PULSE_RESTORE_UNIT',
    traceLabels: { active: 'restoring', done: 'restored', unit: 'unit' },
    targetEntityType: 'ABSOLUTE',
    description: 'Undoes a split or a merge in the scenario on the map: give any unit it took away or created. The moves on the units it created are removed.',
    parameters: [{ name: 'shopId', type: 'string', description: 'A unit the split or merge involved.', required: true }],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_RESTORE_UNIT';
      const { k } = openPlan(action, kernel, options.sessionId);
      const id = typeof params.shopId === 'string' ? params.shopId.trim() : '';
      if (!removeLayoutChange(k, id)) throw new Error(`${action}: ${id} is not part of a split or merge in this scenario.`);
    },
  },

  PULSE_CREATE_SCENARIO: {
    action_type: 'PULSE_CREATE_SCENARIO',
    traceLabels: { active: 'drafting', done: 'drafted', unit: 'scenario' },
    targetEntityType: 'ABSOLUTE',
    description: 'Creates a scenario in the open plan and puts it on the map; the PULSE_SET_MOVE commands that follow fill it. A plan holds at most 3 scenarios. Give each a distinct strategy and write its reasoning in "thesis": what the data shows, the strategy, the moves it implies and what it should be worth.',
    parameters: [
      { name: 'name', type: 'string', description: 'The scenario\'s theme in ONE word, e.g. "Leisure", "Food", "Value", "Vacancies". The plan numbers scenarios itself and shows it as "Scenario 2 · Leisure"; put the strategy in the thesis.', required: true },
      { name: 'thesis', type: 'string', description: 'The reasoning the user reads beside the scenario: evidence, strategy, expected effect. Plain sentences.', required: true },
      { name: 'startFrom', type: 'string', description: '"empty" (default) or "current" to start from a copy of the scenario on the map.', required: false, options: ['empty', 'current'] },
    ],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_CREATE_SCENARIO';
      const { k } = openPlan(action, kernel, options.sessionId);
      if (typeof params.name !== 'string' || !/^\S{1,24}$/.test(params.name.trim())) throw new Error(`${action}: name is the scenario's theme in one word (e.g. "Leisure"), got "${params.name}".`);
      const created = createScenario(k, params.name, typeof params.thesis === 'string' ? params.thesis : '', params.startFrom === 'current' ? 'current' : 'empty');
      if (typeof created !== 'string') throw new Error(`${action}: ${created.error}`);
      patchPulse(k, { section: 'scenarios', agentTouched: [] });
    },
  },

  PULSE_SWITCH_SCENARIO: {
    action_type: 'PULSE_SWITCH_SCENARIO',
    traceLabels: { active: 'opening', done: 'opened', unit: 'scenario' },
    targetEntityType: 'ABSOLUTE',
    description: 'Puts another scenario of the plan on the map; the move commands then act on it.',
    parameters: [{ name: 'scenario', type: 'string', description: 'Scenario id or exact name, from the SCENARIOS block.', required: true }],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_SWITCH_SCENARIO';
      const { k } = openPlan(action, kernel, options.sessionId);
      const scenarios = k.getState().pulse.draft?.scenarios ?? [];
      const asked = String(params.scenario ?? '').toLowerCase();
      const hit = scenarios.find(s => s.id === params.scenario) ?? scenarios.find((s, i) => s.name.toLowerCase() === asked || scenarioLabel(i, s.name).toLowerCase() === asked);
      if (!hit) throw new Error(`${action}: no scenario "${params.scenario}". Scenarios: ${scenarios.map(s => `${s.id} (${s.name})`).join(', ')}.`);
      switchScenario(k, hit.id);
    },
  },

  PULSE_SET_SCENARIO_THESIS: {
    action_type: 'PULSE_SET_SCENARIO_THESIS',
    traceLabels: { active: 'writing', done: 'wrote', unit: 'reasoning' },
    targetEntityType: 'ABSOLUTE',
    description: 'Rewrites the reasoning shown beside the scenario on the map, e.g. once its moves are placed and priced: the evidence, the strategy, and its value created, uplift and occupancy as read back from the plan.',
    parameters: [{ name: 'thesis', type: 'string', description: 'The full reasoning, plain sentences.', required: true }],
    availableInModes: ['pulse'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const action = 'PULSE_SET_SCENARIO_THESIS';
      const { k } = openPlan(action, kernel, options.sessionId);
      const id = k.getState().pulse.draft?.activeScenarioId;
      if (!id || typeof params.thesis !== 'string' || !params.thesis.trim()) throw new Error(`${action}: give the reasoning as text.`);
      setScenarioThesis(k, id, params.thesis);
    },
  },
};

export const pulseCommandTimeEstimations: Record<string, number> = {
  PULSE_READ_TRAFFIC_EVIDENCE: 2, PULSE_SELECT_SHOP: 1, PULSE_SET_MOVE: 1, PULSE_REMOVE_MOVE: 1, PULSE_SET_ASSUMPTIONS: 1, PULSE_SET_VIEW: 1, PULSE_ZOOM: 1, PULSE_SPLIT_UNIT: 1, PULSE_MERGE_UNITS: 1, PULSE_RESTORE_UNIT: 1, PULSE_CREATE_SCENARIO: 1, PULSE_SWITCH_SCENARIO: 1, PULSE_SET_SCENARIO_THESIS: 1,
};
